import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Box,
	FormControl,
	Grid2 as Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from "@mui/material";
import PageContext from "../../components/Common/PageContext";
import LabelSheetStartPicker from "../../components/Labels/LabelSheetStartPicker";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { ModalBase } from "../../components/Modal/ModalBase";
import PrinterSelect from "../../components/Printer/PrinterSelect";
import { LabelConfiguration } from "../../common/api/Models/LabelConfiguration";
import { LargeActionButton } from "../../components/Common/LargeActionButton";
import { getLabelConfigurations, postTagSheetLabelRequests } from "../../services/apiHelper";
import { appInsights } from "../../services/telemetryService";
import alertTypeEnum from "../../util/alertTypeEnum";
import { translateError } from "../../util/axiosUtils";
import { isTagSheetLabelConfiguration } from "../../util/labelConfigurationUtil";
import { PrinterOutputType } from "../../util/printerEnums";

const MAX_LABEL_NUMBER = 9999;

const sanitizeNumericInput = (value: string) => {
	return value.replace(/\D/g, "").slice(0, 4);
};

export default function TagSheetLabels() {
	const [pageContext, updatePageContextAsync] = useContext(PageContext);
	const { selectedLabelPrinterId, printers, userLocation } = pageContext;
	const [selectedPrinter, setSelectedPrinter] = useState<string | undefined>(selectedLabelPrinterId);
	const [availableTemplates, setAvailableTemplates] = useState<LabelConfiguration[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<LabelConfiguration | undefined>(undefined);
	const [description, setDescription] = useState("");
	const [fromLabelNumber, setFromLabelNumber] = useState("");
	const [toLabelNumber, setToLabelNumber] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [startColumn, setStartColumn] = useState(0);
	const [startRow, setStartRow] = useState(0);
	const [alertModal, setAlertModal] = useState({
		isOpen: false,
		title: "",
		message: "",
		alertType: "",
		primaryButtonName: "",
		secondaryButtonName: "",
	});

	const tagSheetTemplates = useMemo(
		() => availableTemplates.filter(isTagSheetLabelConfiguration),
		[availableTemplates]
	);

	useEffect(() => {
		getLabelConfigurations(userLocation)
			.then((res) => {
				setAvailableTemplates(res.data as LabelConfiguration[]);
			})
			.catch((err) => console.error("Failed to load label configurations", err));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userLocation]);

	useEffect(() => {
		setStartColumn(0);
		setStartRow(0);

		const printerData = printers?.find((p) => p.id === selectedPrinter);
		const outputType = printerData?.outputType;
		const matchingTemplates = outputType
			? tagSheetTemplates.filter((t) => t.printerOutputType === outputType)
			: tagSheetTemplates;

		if (matchingTemplates.length === 1) {
			setSelectedTemplate(matchingTemplates[0]);
		} else {
			setSelectedTemplate(undefined);
		}
	}, [printers, selectedPrinter, tagSheetTemplates]);

	const onPrinterSelect = async (e: SelectChangeEvent<string>) => {
		setSelectedPrinter(e.target.value);
		await updatePageContextAsync({ selectedLabelPrinterId: e.target.value });
	};

	const onTemplateSelect = (e: SelectChangeEvent<string>) => {
		const format = e.target.value;
		const template = tagSheetTemplates.find((t) => t.format === format);
		setSelectedTemplate(template);
		setStartColumn(0);
		setStartRow(0);
	};

	const fromValue = fromLabelNumber === "" ? undefined : parseInt(fromLabelNumber, 10);
	const toValue = toLabelNumber === "" ? undefined : parseInt(toLabelNumber, 10);
	const fromHasError = fromValue !== undefined && (fromValue < 1 || fromValue > MAX_LABEL_NUMBER);
	const toHasError = toValue !== undefined && (toValue < 1 || toValue > MAX_LABEL_NUMBER);
	const hasInvalidRange = fromValue !== undefined && toValue !== undefined && fromValue > toValue;
	const isRangeValid =
		fromValue !== undefined && toValue !== undefined && !fromHasError && !toHasError && !hasInvalidRange;
	const labelCount = isRangeValid ? toValue - fromValue + 1 : 0;

	const printerData = printers?.find((p) => p.id === selectedPrinter);
	const hasA4Templates = tagSheetTemplates.some((template) => template.printerOutputType === PrinterOutputType.A4);
	const hasZebraTemplates = tagSheetTemplates.some(
		(template) => template.printerOutputType === PrinterOutputType.Zebra
	);
	const availablePrinters =
		printers?.filter((printer) => {
			if (printer.outputType === PrinterOutputType.A4) {
				return hasA4Templates;
			}

			if (printer.outputType === PrinterOutputType.Zebra) {
				return hasZebraTemplates;
			}

			return true;
		}) ?? [];
	const filteredTemplates = printerData?.outputType
		? tagSheetTemplates.filter((t) => t.printerOutputType === printerData.outputType)
		: tagSheetTemplates;
	const hasSelectedTemplate =
		!!selectedTemplate && filteredTemplates.some((template) => template.format === selectedTemplate.format);
	const templateRequiresSelection = filteredTemplates.length > 1 && !hasSelectedTemplate;
	const templateSelectionMissing = filteredTemplates.length > 0 && !hasSelectedTemplate;
	const isPrintButtonDisabled =
		!isRangeValid || !selectedPrinter || filteredTemplates.length === 0 || templateSelectionMissing;

	useEffect(() => {
		if (!selectedPrinter) {
			return;
		}

		const selectedPrinterIsAvailable = availablePrinters.some((printer) => printer.id === selectedPrinter);
		if (selectedPrinterIsAvailable) {
			return;
		}

		setSelectedPrinter(undefined);
		updatePageContextAsync({ selectedLabelPrinterId: undefined });
	}, [availablePrinters, selectedPrinter, updatePageContextAsync]);

	const showSuccessModal = (title: string, message: string) => {
		setAlertModal({
			isOpen: true,
			title,
			message,
			alertType: alertTypeEnum.SUCCESS,
			primaryButtonName: "OK",
			secondaryButtonName: "",
		});
	};

	const closeModal = () => {
		setAlertModal({
			isOpen: false,
			title: "",
			message: "",
			alertType: alertTypeEnum.INFO,
			primaryButtonName: "",
			secondaryButtonName: "",
		});
	};

	const handlePrint = async () => {
		if (!isRangeValid || !selectedPrinter || !hasSelectedTemplate || fromValue === undefined || toValue === undefined) {
			return;
		}

		setIsLoading(true);
		await postTagSheetLabelRequests(
			selectedPrinter,
			fromValue,
			toValue,
			userLocation,
			printerData?.outputType,
			startColumn,
			startRow,
			selectedTemplate.format,
			description || undefined
		)
			.then(() => {
				appInsights.trackEvent(
					{ name: "TagSheetLabelsPrinted" },
					{
						locationId: userLocation,
						format: selectedTemplate.format,
						labelCount: toValue - fromValue + 1,
						printerId: selectedPrinter,
						printerName: printerData?.name,
					}
				);
				showSuccessModal("Success", "Tag sheet labels printed successfully");
			})
			.catch(async (error) => {
				await updatePageContextAsync({
					snackBarAlert: {
						isOpen: true,
						message: translateError(error),
						severity: "error",
					},
				});
			})
			.finally(() => setIsLoading(false));
	};

	return (
		<>
			<LoadingDialog isOpen={isLoading} message="Loading" />
			<ModalBase
				{...alertModal}
				onPrimaryButtonClick={closeModal}
				onSecondaryButtonClick={closeModal}
				onClose={closeModal}
			/>

			<Grid container spacing={2} sx={{ pt: 1 }}>
				<Grid size={{ xs: 12 }}>
					<TextField
						fullWidth
						label="Description"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						helperText=" "
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<TextField
						fullWidth
						label="From"
						type="number"
						value={fromLabelNumber}
						onChange={(e) => setFromLabelNumber(sanitizeNumericInput(e.target.value))}
						error={fromHasError}
						helperText={fromHasError ? "Enter a number between 1 and 9999." : " "}
						slotProps={{ htmlInput: { min: 1, max: MAX_LABEL_NUMBER } }}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<TextField
						fullWidth
						label="To"
						type="number"
						value={toLabelNumber}
						onChange={(e) => setToLabelNumber(sanitizeNumericInput(e.target.value))}
						error={toHasError}
						helperText={toHasError ? "Enter a number between 1 and 9999." : " "}
						slotProps={{ htmlInput: { min: 1, max: MAX_LABEL_NUMBER } }}
					/>
				</Grid>
			</Grid>

			{hasInvalidRange && (
				<Typography color="error" variant="body2" sx={{ mt: 1 }}>
					The end of the range must be greater than or equal to the start.
				</Typography>
			)}

			{isRangeValid && (
				<Typography variant="body2" sx={{ mt: 1 }}>
					This range will print {labelCount} labels.
				</Typography>
			)}

			{tagSheetTemplates.length === 0 && (
				<Typography color="warning.main" variant="body2" sx={{ mt: 2 }}>
					No tag sheet label templates are available for this location.
				</Typography>
			)}

			<Grid style={{ marginTop: 10 }}>
				<PrinterSelect
					title="Label Printer"
					printers={availablePrinters}
					handleChange={onPrinterSelect}
					selectedPrinterId={selectedPrinter ?? ""}
				/>
			</Grid>

			{selectedPrinter && filteredTemplates.length > 0 && (
				<Box sx={{ mt: 2 }}>
					<FormControl fullWidth size="small">
						<InputLabel id="tag-sheet-template-select-label">Label Template</InputLabel>
						<Select
							labelId="tag-sheet-template-select-label"
							label="Label Template"
							value={selectedTemplate?.format ?? ""}
							onChange={onTemplateSelect}
						>
							{filteredTemplates.map((template) => (
								<MenuItem key={template.format} value={template.format}>
									{template.friendlyName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{templateRequiresSelection && (
						<Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
							Please select a template to continue.
						</Typography>
					)}
				</Box>
			)}

			{selectedPrinter && selectedTemplate?.printerOutputType === PrinterOutputType.A4 && (
				<Box sx={{ my: 2 }}>
					<LabelSheetStartPicker
						columns={selectedTemplate.sheetColumns}
						rows={selectedTemplate.sheetRows}
						startColumn={startColumn}
						startRow={startRow}
						onChange={(column, row) => {
							setStartColumn(column);
							setStartRow(row);
						}}
					/>
				</Box>
			)}

			<Grid style={{ marginTop: 10 }}>
				<LargeActionButton onClick={handlePrint} disabled={isPrintButtonDisabled}>
					Print
				</LargeActionButton>
			</Grid>
		</>
	);
}
